export interface CarouselOffsetInput {
  /** Width of the clipping window. */
  windowWidth: number;
  /** Width of the dot strip, including its own padding. */
  contentWidth: number;
  /** Left edge of the active dot, in the strip's coordinates. */
  activeStart: number;
  /** Right edge of the active dot, in the strip's coordinates. */
  activeEnd: number;
  /**
   * Space to keep after the active dot when shifting, so it does not sit on the clip.
   * Ignored once the strip is shifted as far as it can go.
   */
  endInset: number;
  /**
   * Space between a visible end of the strip and the widget edge. Same distance as the gap
   * between two dots. An end that is clipped off sits flush with the edge instead.
   */
  sidePadding: number;
}

/**
 * Horizontal shift for a dot strip inside a clipping window.
 *
 * A strip that fits is centered, at least `sidePadding` in from each edge. A strip that
 * overflows keeps that gap on an end that is still on screen, and runs flush to the widget
 * edge on an end that is clipped. It shifts left only far enough to keep the active dot inside.
 */
export function carouselOffset({
  windowWidth,
  contentWidth,
  activeStart,
  activeEnd,
  endInset,
  sidePadding,
}: CarouselOffsetInput): number {
  if (windowWidth <= 0) return 0;

  if (contentWidth + sidePadding * 2 <= windowWidth) {
    return sidePadding + (windowWidth - sidePadding * 2 - contentWidth) / 2;
  }
  if (contentWidth <= windowWidth) return (windowWidth - contentWidth) / 2;

  const minOffset = windowWidth - contentWidth;
  const needed = windowWidth - activeEnd - endInset;
  let offset = Math.min(0, Math.max(minOffset, needed));

  // The active dot is wider than the window. Pin its start to the left edge.
  if (activeStart + offset < 0) {
    offset = Math.min(0, Math.max(minOffset, -activeStart));
  }

  // A visible end of the strip keeps the gap. A clipped end stays flush with the widget edge.
  if (offset >= 0) return sidePadding;
  if (offset + contentWidth <= windowWidth) return minOffset - sidePadding;

  return offset;
}
